import React from 'react';
import { Box, Button, Chip, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEllipsisVertical,
    faTimes,
    faUnlink,
    faUpload,
    faChevronLeft,
    faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { WorkItem } from '../api/workItems';
import { formatListPosition } from '../utils/drawerChromeCopy';

export type SourceDocumentState = 'none' | 'checking' | 'available' | 'deleted' | 'missing' | 'unknown_error';

export interface DrawerHeaderNavigation {
    index: number;
    total: number;
    /** List being scrolled. Blank hides the position chip so it cannot read as a bare fraction. */
    pileLabel?: string;
    hasPrev: boolean;
    hasNext: boolean;
    onPrev: () => void;
    onNext: () => void;
    disabled?: boolean;
}

interface WorkItemDrawerHeaderProps {
    item: WorkItem | null;
    /** Scrolled down or the original email open: the title drops to 18 px, 2 lines (D4). */
    compactHeaderActive: boolean;
    motionTransition: string;
    sourceDocumentState: SourceDocumentState;
    sourceDocumentMessage: string;
    /** ⋮ is offered: never on a new item, and on the source overlay only for its document (D6). */
    showActionsMenu: boolean;
    onOpenActionsMenu: (event: React.MouseEvent<HTMLElement>) => void;
    onClose: () => void;
    onOpenUploadReplacement: () => void;
    onUnlinkSourceDocument: () => void;
    getTitle: (item: WorkItem) => string;
    /** Desktop only: on a phone Mark complete and Reopen sit in the drawer's bottom bar. */
    primaryActionLabel?: string;
    primaryActionIcon?: React.ReactNode;
    primaryActionColor?: 'primary' | 'success' | 'warning' | 'error' | 'info';
    onPrimaryAction?: () => void;
    primaryActionDisabled?: boolean;
    /** Present only when the host list has more than one item. */
    navigation?: DrawerHeaderNavigation;
    detailsControl?: React.ReactNode;
    /** Verified family sentence. Null hides the claim — do not fall back to a stored count. */
    derivedTasks?: string | null;
    titleOverride?: string | null;
    /** Virtual source overlay: the loaded row is still the child, so hide writes. */
    suppressChildActions?: boolean;
    /** Email / Document / Note. Omitted when the source is unknown. */
    sourceChip?: string | null;
    /** Life area, already humanized. */
    lifeAreaChip?: string | null;
    /** Due and state on one quiet line under the title. */
    dueStatusLine?: string | null;
}

const iconButtonSx = { minWidth: 44, minHeight: 44, color: 'var(--color-text-secondary)' } as const;
const focusRingSx = {
    '&:focus-visible': {
        outline: '2px solid var(--color-accent)',
        outlineOffset: '2px',
    },
} as const;

/**
 * Drawer header (D2): source and life-area chips with the controls on one row, the title
 * at full width under them, then the due / status line and the derived-tasks line. On a
 * phone the row keeps only ⋮ and Close; Source details, list position, previous and next
 * share one row under the title.
 */
const WorkItemDrawerHeader: React.FC<WorkItemDrawerHeaderProps> = ({
    item,
    compactHeaderActive,
    motionTransition,
    sourceDocumentState,
    sourceDocumentMessage,
    showActionsMenu,
    onOpenActionsMenu,
    onClose,
    onOpenUploadReplacement,
    onUnlinkSourceDocument,
    getTitle,
    primaryActionLabel,
    primaryActionIcon,
    primaryActionColor = 'primary',
    onPrimaryAction,
    primaryActionDisabled = false,
    navigation,
    detailsControl,
    derivedTasks = null,
    titleOverride = null,
    suppressChildActions = false,
    sourceChip = null,
    lifeAreaChip = null,
    dueStatusLine = null,
}) => {
    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
    const positionLabel = navigation
        ? formatListPosition(navigation.index + 1, navigation.total, navigation.pileLabel ?? '')
        : null;
    const title = titleOverride || (item ? getTitle(item) : 'New quick item');
    const titleSize = compactHeaderActive ? '1.125rem' : isPhone ? '1.375rem' : '1.5rem';
    const showPrimary = Boolean(!isPhone && primaryActionLabel && onPrimaryAction);

    const position = positionLabel && (
        <Typography
            component="span"
            data-testid="WB_DRAWER_NAV_POSITION"
            aria-label={positionLabel}
            sx={{
                fontSize: '0.75rem',
                fontWeight: 500,
                fontVariantNumeric: 'tabular-nums',
                color: 'var(--color-text-muted)',
                whiteSpace: 'nowrap',
                flexShrink: 0,
            }}
        >
            {positionLabel}
        </Typography>
    );

    const navButtons = navigation && (
        <>
            <Tooltip title="Previous item (k)">
                <span>
                    <IconButton
                        onClick={navigation.onPrev}
                        disabled={navigation.disabled || !navigation.hasPrev}
                        aria-label="Previous item"
                        data-testid="WB_DRAWER_NAV_PREV"
                        sx={iconButtonSx}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} size="sm" />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title="Next item (j)">
                <span>
                    <IconButton
                        onClick={navigation.onNext}
                        disabled={navigation.disabled || !navigation.hasNext}
                        aria-label="Next item"
                        data-testid="WB_DRAWER_NAV_NEXT"
                        sx={iconButtonSx}
                    >
                        <FontAwesomeIcon icon={faChevronRight} size="sm" />
                    </IconButton>
                </span>
            </Tooltip>
        </>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: compactHeaderActive ? 0.5 : 1, transition: `gap ${motionTransition}` }}>
            {/* Row 1: chips, then the controls at the right. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 44 }}>
                {item && sourceChip && (
                    <Chip
                        size="small"
                        label={sourceChip}
                        data-testid="WB_DRAWER_SOURCE_CHIP"
                        sx={{
                            height: 22,
                            fontSize: '0.7rem',
                            fontWeight: 650,
                            color: 'var(--color-accent-text)',
                            bgcolor: 'var(--color-accent-muted)',
                            border: '1px solid rgba(var(--color-accent-rgb), 0.35)',
                            flexShrink: 0,
                        }}
                    />
                )}
                {item && lifeAreaChip && (
                    <Chip
                        size="small"
                        label={lifeAreaChip}
                        data-testid="WB_DRAWER_LIFE_AREA_CHIP"
                        sx={{
                            height: 22,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: 'var(--color-text-secondary)',
                            bgcolor: 'var(--color-bg-card)',
                            border: '1px solid var(--color-border)',
                            minWidth: 0,
                            ml: sourceChip ? 0.25 : 0,
                        }}
                    />
                )}
                <Box sx={{ flexGrow: 1 }} />
                {showPrimary && (
                    <Button
                        variant="contained"
                        size="small"
                        color={primaryActionColor}
                        onClick={onPrimaryAction}
                        disabled={primaryActionDisabled}
                        data-testid="WB_DRAWER_PRIMARY_ACTION"
                        sx={{
                            mr: 0.5,
                            minHeight: 44,
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                            textTransform: 'none',
                            ...(primaryActionLabel === 'Start' ? {
                                bgcolor: 'var(--color-accent)',
                                color: 'var(--color-on-accent)',
                                '&:hover': {
                                    bgcolor: 'var(--color-accent-hover)',
                                    color: 'var(--color-on-accent-hover)',
                                },
                            } : {}),
                            ...focusRingSx,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            {primaryActionIcon}
                            <span>{primaryActionLabel}</span>
                        </Box>
                    </Button>
                )}
                {!isPhone && navButtons}
                {item && showActionsMenu && (
                    <Tooltip title="Actions">
                        <IconButton
                            onClick={onOpenActionsMenu}
                            sx={iconButtonSx}
                            data-testid="WB_DRAWER_ACTIONS_MENU_BUTTON"
                        >
                            <FontAwesomeIcon icon={faEllipsisVertical} size="sm" />
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title="Close">
                    <IconButton onClick={onClose} sx={{ ...iconButtonSx, ...focusRingSx }}>
                        <FontAwesomeIcon icon={faTimes} size="sm" />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* The stored title is never cut: the full text is the heading's content and its tooltip. */}
            <Typography
                id="wb-drawer-title"
                component="h2"
                variant="h6"
                fontWeight="bold"
                gutterBottom={false}
                title={title}
                sx={{
                    m: 0,
                    pr: isPhone ? 1.5 : 0,
                    color: 'var(--color-text-primary)',
                    wordBreak: 'normal',
                    overflowWrap: 'anywhere',
                    lineHeight: 1.25,
                    fontSize: titleSize,
                    letterSpacing: '-0.01em',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: compactHeaderActive ? 2 : 3,
                    overflow: 'hidden',
                    transition: `font-size ${motionTransition}`,
                }}
            >
                {title}
            </Typography>

            {(dueStatusLine || (!isPhone && position)) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, pr: isPhone ? 1.5 : 0 }}>
                    {dueStatusLine && (
                        <Typography
                            component="p"
                            data-testid="WB_DRAWER_DUE_STATUS"
                            sx={{
                                m: 0,
                                flex: '1 1 auto',
                                minWidth: 0,
                                fontSize: '0.8125rem',
                                fontWeight: 500,
                                color: 'var(--color-text-secondary)',
                                lineHeight: 1.4,
                            }}
                        >
                            {dueStatusLine}
                        </Typography>
                    )}
                    {!isPhone && (
                        <>
                            {!dueStatusLine && <Box sx={{ flexGrow: 1 }} />}
                            {position}
                        </>
                    )}
                </Box>
            )}

            {!isPhone && (derivedTasks || detailsControl) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 32 }}>
                    {derivedTasks ? (
                        <Typography
                            variant="caption"
                            component="p"
                            data-testid="WB_DRAWER_DERIVED_TASKS"
                            sx={{ m: 0, flex: '1 1 auto', minWidth: 0, color: 'var(--color-text-secondary)', fontWeight: 500 }}
                        >
                            {derivedTasks}
                        </Typography>
                    ) : <Box sx={{ flexGrow: 1 }} />}
                    {detailsControl}
                </Box>
            )}

            {isPhone && derivedTasks && (
                <Typography
                    variant="caption"
                    component="p"
                    data-testid="WB_DRAWER_DERIVED_TASKS"
                    sx={{ m: 0, pr: 1.5, color: 'var(--color-text-secondary)', fontWeight: 500 }}
                >
                    {derivedTasks}
                </Typography>
            )}

            {isPhone && (detailsControl || position || navigation) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 44, ml: -1 }}>
                    {detailsControl}
                    <Box sx={{ flexGrow: 1 }} />
                    {position}
                    {navButtons}
                </Box>
            )}

            {item?.document_id && ['deleted', 'missing', 'unknown_error'].includes(sourceDocumentState) && (
                <Box
                    data-testid="WB_SOURCE_UNAVAILABLE"
                    role="status"
                    sx={{
                        mt: 0.5,
                        mb: 0.5,
                        mr: isPhone ? 1.5 : 0,
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid var(--color-border)',
                        bgcolor: 'var(--color-bg-card)',
                        display: 'grid',
                        gap: 1,
                    }}
                >
                    <Typography variant="body2" sx={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>
                        Source document unavailable
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
                        {sourceDocumentMessage || "This file was removed or can't be found."}
                    </Typography>
                    {!suppressChildActions && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<FontAwesomeIcon icon={faUpload} />}
                            onClick={onOpenUploadReplacement}
                        >
                            Manage documents
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<FontAwesomeIcon icon={faUnlink} />}
                            onClick={onUnlinkSourceDocument}
                        >
                            Remove link
                        </Button>
                    </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default WorkItemDrawerHeader;
